import React, { useCallback, useEffect, useMemo, useState } from "react";
import { TextField, IconButton } from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import Autocomplete from '@mui/material/Autocomplete';
import ExpandMore from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import { MultiSelectList } from "./Editor/MultiSelectList";

export default function MultiSizeDropdown({ options }) {
  const {
    value = [],
    onChange,
    handleChange,
    label = "Banner Sizes",
    disabled = false,
    required = false,
    name = "bannerSize",
    items = [],
    className,
    listVariant = "checkbox",
    displayValue,
  } = options || {};

  const allOptions = useMemo(
    () =>
      (items || []).map((lbl) => ({
        id: String(lbl),
        displayName: String(lbl),
        value: String(lbl),
        label: String(lbl),
        name: String(lbl),
      })),
    [items]
  );

  const [open, setOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [optionsToDisplay, setOptionsToDisplay] = useState(allOptions);

  const normalizedSelected = useMemo(
    () =>
      (Array.isArray(value) ? value : []).map((lbl) => ({
        id: String(lbl),
        displayName: String(lbl),
        value: String(lbl),
      })),
    [value]
  );
  const [selected, setSelected] = useState(normalizedSelected);

  useEffect(() => setOptionsToDisplay(allOptions), [allOptions]);

  useEffect(() => {
    setSelected(normalizedSelected);
    if (!normalizedSelected.length) setSearchText("");
  }, [normalizedSelected]);

  useEffect(() => {
    const q = String(searchText || "").toLowerCase();
    setOptionsToDisplay(
      allOptions.filter((el) =>
        String(el?.displayName || "")
          .toLowerCase()
          .includes(q)
      )
    );
  }, [searchText, allOptions]);

  useEffect(() => {
    if (!open && searchText) setSearchText("");
  }, [open, searchText]);

  const useStyles = makeStyles(() => ({
    paper: { overflowY: "hidden", fontSize: 12 },
    autocomplete: {
      fontFamily: "Nunito, sans-serif !important",
      "& .MuiAutocomplete-inputRoot": {
        "--end-gap": 96,
        height: 42,
        marginBottom: 16,
        fontSize: 12,
        flexWrap: "nowrap",
        position: "relative",
        boxSizing: "border-box",
      },
      "& .MuiFilledInput-input::placeholder": { color: "#8A8A8A", opacity: 1 },
      "& .MuiFormLabel-root.Mui-disabled": { color: "#878888 !important" },
      "& .MuiFilledInput-root": {
        backgroundColor: "#ffffff",
        height: 42,
        fontSize: 12,
        border: "solid 1px #d4d4d4",
        paddingRight: "calc(var(--end-gap) * 1px) !important",
        paddingLeft: "8px !important",
        borderRadius: 6,
        paddingBottom: 0,
        paddingTop: 10,
        display: "flex",
        alignItems: "flex-start",
        cursor: "text",
        boxSizing: "border-box",
      },
      "& .MuiFilledInput-root.Mui-disabled": {
        border: "none",
      },
      "& .MuiFilledInput-input": {
        lineHeight: "20px",
        height: 20,
        transition: "none",
        paddingRight: 5,
        padding: "0 !important",
        color: "#000000",
        marginTop: 8,
        boxSizing: "border-box",
        minWidth: 0,
      },
      "& .MuiInputBase-input.Mui-disabled": {
        color: "#878888 !important",
        WebkitTextFillColor: "#878888",
      },
      "& .MuiAutocomplete-input": { minWidth: 0 },
      "& .MuiInputLabel-formControl": { top: "-4px", zIndex: 1 },
      "& .MuiFilledInput-underline:before": { borderBottom: "none" },
      "& .MuiFilledInput-underline:after": { borderBottom: "none" },
      "& .MuiFilledInput-underline:hover:before": { borderBottom: "none" },
      "& .MuiInputLabel-filled.MuiInputLabel-shrink": {
        transform: " translate(9px, 12px) scale(0.75)",
        color: "#54565B",
      },
      "& .MuiFormLabel-asterisk": { color: "#e32b35" },
      "& .MuiFormLabel-root": {
        fontSize: 12,
        color: "#1E1E1E",
        fontWeight: 400,
        fontFamily: "Nunito, sans-serif !important",
        top: "-5.7px !important",
        zIndex: 2,
      },
      "& .MuiFormLabel-root.Mui-focused": {
        color: "#54565B !important",
      },
      "& .MuiAutocomplete-clearIndicator": { display: "none" },
      "& .MuiAutocomplete-startAdornment": {
        display: "flex",
        alignItems: "center",
        flexWrap: "nowrap",
        gap: 4,
        overflow: "hidden",
        whiteSpace: "nowrap",
        textOverflow: "ellipsis",
        maxWidth: "100%",
        paddingRight: "calc(var(--end-gap) * 1px)",
        pointerEvents: "none",
      },
      "& .MuiAutocomplete-tag": {
        backgroundColor: "#e3f2fd",
        maxWidth: "100%",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        padding: 0,
        margin: 0,
      },
      "& .MuiAutocomplete-endAdornment": { display: "none" },
      "& .MuiAutocomplete-popupIndicator": { display: "none" },
    },
    selectedText: {
      color: "#231F20",
      fontSize: 12,
      fontFamily: "Nunito, sans-serif",
      fontWeight: "normal",
      marginRight: 4,
      lineHeight: "20px",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      maxWidth: "100%",
      marginTop: 8,
      height: 20,
      display: "flex",
      alignItems: "center",
      pointerEvents: "none",
    },
    listbox: {
      maxHeight: 200,
      overflowY: "auto",
      scrollbarWidth: "thin",
      scrollbarColor: "#C8CDD4 transparent",
      "&::-webkit-scrollbar": { width: 6 },
      "&::-webkit-scrollbar-thumb": {
        backgroundColor: "#C8CDD4",
        borderRadius: 8,
        minHeight: 24,
      },
      "&::-webkit-scrollbar-thumb:hover": { backgroundColor: "#AEB5BE" },
      "&::-webkit-scrollbar-track": { background: "transparent" },
      "&::-webkit-scrollbar-button": { display: "none !important" },
      "& .MuiAutocomplete-option, \
         & .MuiMenuItem-root, \
         & .MuiListItem-root, \
         & li[role='option'], \
         & div[role='option']": {
        borderBottom: "none !important",
      },
      "& .MuiDivider-root": { display: "none !important" },
    },
    countPill: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      minWidth: 18,
      padding: "0 6px",
      height: 16,
      borderRadius: 16,
      backgroundColor: "#F0F5F8",
      color: "#203662",
      fontWeight: 700,
      fontSize: 11,
      lineHeight: "16px",
      userSelect: "none",
    },
    endPill: { marginRight: 6 },
    clearBtn: {
      padding: 4,
      borderRadius: 6,
      "& svg": { fontSize: 18, color: "#203662" },
      "&:hover svg": { opacity: 0.9 },
    },
    dropdownBtn: {
      padding: 4,
      borderRadius: 6,
      "& svg": { fontSize: 20, color: "#203662" },
      "&:hover svg": { opacity: 0.9 },
    },
    endAdornmentContainer: {
      position: "absolute",
      right: 4,
      top: "50%",
      transform: "translateY(-50%)",
      display: "flex",
      alignItems: "center",
      gap: 2,
      pointerEvents: "auto",
    },
  }));
  const classes = useStyles();
  const emitChange = useCallback(
    (arr) => {
      const payload = (arr || []).map((o) =>
        typeof o === "string" ? o : o?.value
      );
      if (handleChange) {
        handleChange({ target: { name, value: payload } });
      } else if (onChange) {
        onChange(payload);
      }
    },
    [handleChange, onChange, name]
  );

  const handleClearAll = useCallback(() => {
    // Clear only the visible field value; keep parent/API selection unchanged.
    setSelected([]);
    setSearchText("");
  }, []);

  const handleSelectionChange = useCallback(
    (isChecked, option) => {
      const curr = selected || [];
      const next = isChecked
        ? curr.find((el) => el.id === option.id)
          ? curr
          : [...curr, option]
        : curr.filter((el) => el.id !== option.id);

      setSelected(next);
      emitChange(next);
    },
    [selected, emitChange]
  );

  const getInputProps = (params) => {
    const count = Math.max(0, (selected?.length || 0) - 1);
    return {
      ...params.InputProps,
      endAdornment: (
        <div className={classes.endAdornmentContainer}>
          {!disabled && count > 0 && (
            <span className={`${classes.countPill} ${classes.endPill}`}>
              +{count}
            </span>
          )}
          {!disabled && selected?.length > 0 && (
            <IconButton
              className={classes.clearBtn}
              size="small"
              aria-label="Clear selected sizes"
              onMouseDown={(e) => e.preventDefault()}
              onClick={(e) => {
                e.stopPropagation();
                handleClearAll();
              }}
            >
              <CloseIcon style={{ width: 18, height: 18 }} />
            </IconButton>
          )}
          {!disabled && (
            <IconButton
              className={classes.dropdownBtn}
              size="small"
              aria-label="Toggle dropdown"
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setOpen((o) => {
                  const next = !o;
                  if (next) setSelected(normalizedSelected);
                  return next;
                });
              }}
            >
              <ExpandMore
                style={{
                  transform: open ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 0.2s ease",
                  width: 18,
                  height: 18,
                }}
              />
            </IconButton>
          )}
        </div>
      ),
    };
  };

  return (
    <div style={{ width: "100%", minWidth: 196, marginTop: 0 }}>
      <Autocomplete
        popupIcon={null}
        forcePopupIcon={false}
        inputValue={open ? searchText : ""}
        onInputChange={(e, v, reason) => {
          if (reason !== "reset") setSearchText(v);
        }}
        disabled={disabled}
        limitTags={1}
        id="size-search-and-select"
        style={{ width: "100%" }}
        open={open}
        onOpen={() => {
          if (disabled) return;
          setSelected(normalizedSelected);
          setOpen(true);
        }}
        onClose={(event, reason) => {
          if (reason !== "toggleInput") setOpen(false);
        }}
        options={optionsToDisplay}
        value={selected || []}
        className={`${classes.autocomplete} ${classes.paper} ${
          className || ""
        }`}
        getOptionLabel={(option) => option.displayName}
        isOptionEqualToValue={(option, val) => option.id === val.id}
        renderInput={(params) => (
          <TextField
            id="tf-size-search-select"
            {...params}
            label={label}
            variant="filled"
            onChange={(e) => setSearchText(e.target.value)}
            required={required}
            onClick={() => {
              setSelected(normalizedSelected);
              setOpen(true);
            }}
            inputProps={{
              ...params.inputProps,
              autoComplete: "off",
              value: searchText,
              placeholder: open ? "Search & Select" : "",
              onClick: (e) => {
                e.stopPropagation();
                setSelected(normalizedSelected);
                setOpen(true);
              },
            }}
            InputProps={getInputProps(params)}
          />
        )}
        renderTags={(vals) => {
          if (disabled || open || !vals?.length) return null;
          const text = displayValue || vals[0]?.displayName || "";
          return (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                flexWrap: "nowrap",
              }}
            >
              <span className={classes.selectedText}>{text}</span>
            </div>
          );
        }}
        multiple
        disableCloseOnSelect
        ListboxComponent={ListboxComponentCustom}
        ListboxProps={{
          className: classes.listbox,
          alreadySelected: selected,
          optionsToDisplay: optionsToDisplay || [],
          open,
          handleSelectionChange,
          listVariant,
          onMouseDown: (e) => e.preventDefault(),
        }}
      />
    </div>
  );
}

const ListboxComponentCustom = React.forwardRef(function ListboxComponentCustom(
  {
    alreadySelected,
    optionsToDisplay,
    open,
    handleSelectionChange,
    listVariant,
    style,
    ...listboxProps
  },
  ref
) {
  return (
    <div ref={ref} {...listboxProps} style={style}>
      <MultiSelectList
        data={optionsToDisplay}
        open={open}
        selected={alreadySelected || []}
        variant={listVariant}
        handleSelection={(isChecked, option) =>
          handleSelectionChange(isChecked, option)
        }
      />
    </div>
  );
});
